import { createFileRoute, Link, notFound, redirect } from "@tanstack/react-router";
import { Phone, MapPin, Clock, CheckCircle2, ArrowRight } from "lucide-react";
import { PageHeader } from "./fordon";
import { getOrt, ORTER, type OrtData } from "@/data/seo-orter";
import { RAPIDO_MODELS } from "@/data/rapido-models";
import { assessOrtModell } from "@/lib/seo-families/ort-modell";

const DEDICATED_ORT_PATHS = {
  kalix: "/husbil-kalix",
  lulea: "/husbil-lulea",
  boden: "/husbil-boden",
  pitea: "/husbil-pitea",
  haparanda: "/husbil-haparanda",
} as const;

function dedicatedOrtPath(slug: string) {
  return DEDICATED_ORT_PATHS[slug as keyof typeof DEDICATED_ORT_PATHS];
}

export const Route = createFileRoute("/husbil/$ort/")({
  codeSplitGroupings: [["loader", "component"]],
  loader: ({ params }) => {
    const dedicatedPath = dedicatedOrtPath(params.ort);
    if (dedicatedPath) {
      throw redirect({ href: dedicatedPath, statusCode: 301 });
    }
    const ort = getOrt(params.ort);
    if (!ort) throw notFound();
    return { ort };
  },
  head: ({ loaderData }) => {
    if (!loaderData) return {};
    const o = loaderData.ort;
    const url = `https://www.akhusbilfritid.se/husbil/${o.slug}`;
    const title = `Husbilar ${o.name} – ${o.driveMin} min till vårt lager`;
    const desc = `Husbil i ${o.name}? Vårt lager i Kalix ligger ${o.distanceKm} km (${o.driveMin} min) bort. Fukttestade och gasoltestade husbilar, transport till ${o.name} ordnas. Rapido-återförsäljare sedan 2004.`;
    return {
      meta: [
        { title },
        { name: "description", content: desc },
        { name: "keywords", content: `${o.focusKeyword}, husbil ${o.name.toLowerCase()}, husvagn ${o.name.toLowerCase()}, husbilshandlare ${o.name.toLowerCase()}, rapido ${o.name.toLowerCase()}` },
        { property: "og:type", content: "website" },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        { property: "og:url", content: url },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: o.faq.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "AutoDealer",
            name: "AK Husbil & Fritid",
            url,
            areaServed: [o.name, o.region, "Kalix"],
            address: {
              "@type": "PostalAddress",
              streetAddress: "Stabsvägen 5",
              addressLocality: "Kalix",
              postalCode: "952 51",
              addressCountry: "SE",
            },
            telephone: "+46708629743",
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Hem", item: "https://www.akhusbilfritid.se" },
              { "@type": "ListItem", position: 2, name: `Husbil ${o.name}`, item: url },
            ],
          }),
        },
      ],
    };
  },
  notFoundComponent: () => (
    <div className="max-w-2xl mx-auto p-8 text-center">
      <h1 className="text-2xl font-bold mb-4">Orten hittades inte</h1>
      <Link to="/" className="text-primary underline">Till startsidan</Link>
    </div>
  ),
  component: OrtPage,
});

function OrtPage() {
  const data = Route.useLoaderData() as { ort: OrtData };
  const o = data.ort;
  const otherOrter = ORTER.filter((x) => x.slug !== o.slug).slice(0, 6);
  // Bara kombinationer som klarar kvalitetsgrinden (samma urval som sitemapen).
  const ortModeller = RAPIDO_MODELS.filter((m) => assessOrtModell(o, m).passed);

  return (
    <>
      <PageHeader
        label={o.region}
        title={`Husbil ${o.name}`}
        sub={`${o.distanceKm} km / ${o.driveMin} min från Kalix — Rapido-återförsäljare och verkstad`}
      />

      <main className="max-w-4xl mx-auto px-4 py-10 space-y-12">

        {/* Breadcrumbs */}
        <nav className="text-sm text-muted-foreground">
          <Link to="/" className="hover:underline">Hem</Link>
          <span className="mx-2">/</span>
          <span>Husbil {o.name}</span>
        </nav>

        {/* Intro */}
        <section>
          <p className="text-lg leading-relaxed">{o.intro}</p>
        </section>

        {/* Snabbfakta */}
        <section className="grid sm:grid-cols-3 gap-4">
          <div className="rounded-lg border p-4">
            <MapPin className="w-5 h-5 text-primary mb-2" />
            <div className="text-xs text-muted-foreground">Avstånd</div>
            <div className="font-semibold">{o.distanceKm} km</div>
          </div>
          <div className="rounded-lg border p-4">
            <Clock className="w-5 h-5 text-primary mb-2" />
            <div className="text-xs text-muted-foreground">Restid</div>
            <div className="font-semibold">{o.driveMin} min</div>
          </div>
          <div className="rounded-lg border p-4">
            <CheckCircle2 className="w-5 h-5 text-primary mb-2" />
            <div className="text-xs text-muted-foreground">Region</div>
            <div className="font-semibold">{o.region}</div>
          </div>
        </section>

        {/* Varför oss */}
        <section>
          <h2 className="text-2xl font-bold mb-4">Varför {o.name}-kunder väljer oss</h2>
          <ul className="space-y-2">
            {o.varforOss.map((v, i) => (
              <li key={i} className="flex gap-3">
                <CheckCircle2 className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                <span>{v}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* Camping & ställplatser */}
        <section>
          <h2 className="text-2xl font-bold mb-4">Camping & ställplatser i {o.name}</h2>
          <p className="mb-4">{o.stallplatser}</p>
          <div className="flex flex-wrap gap-2">
            {o.campingPlaces.map((c) => (
              <span key={c} className="px-3 py-1.5 rounded-full bg-muted text-sm">{c}</span>
            ))}
          </div>
        </section>

        {/* Vinter */}
        <section>
          <h2 className="text-2xl font-bold mb-4">Vinter & förvaring i {o.region}</h2>
          <p>{o.vinter}</p>
        </section>

        {/* Handlare i orten */}
        <section>
          <h2 className="text-2xl font-bold mb-4">Husbilshandlare i {o.name}</h2>
          <p>{o.handlareIOrt}</p>
        </section>

        {/* FAQ */}
        <section>
          <h2 className="text-2xl font-bold mb-6">Vanliga frågor — husbil {o.name}</h2>
          <div className="space-y-4">
            {o.faq.map((f, i) => (
              <div key={i} className="border rounded-lg p-4">
                <h3 className="font-semibold mb-2">{f.q}</h3>
                <p className="text-muted-foreground">{f.a}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Aktuellt lager */}
        <section className="rounded-xl border-2 border-primary/20 bg-muted/40 p-6">
          <h2 className="text-xl font-bold mb-2">Aktuella husbilar för {o.name}-kunder</h2>
          <p className="text-muted-foreground mb-4">
            Vi uppdaterar lagret dagligen på Fyndanu — kontrollerade begagnade husbilar som ligger redo i Kalix, {o.distanceKm} km från {o.name}.
          </p>
          <div className="flex flex-wrap gap-3">
            <a
              href="https://www.fyndanu.se/butik/e435da0b-10d0-46fa-a389-702295c89840"
              target="_blank"
              rel="noopener"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-primary text-primary-foreground font-semibold hover:opacity-90"
            >
              Se lagret på Fyndanu <ArrowRight className="w-4 h-4" />
            </a>
            <Link to="/husbilar-till-salu" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg border font-semibold hover:bg-muted">
              Alla husbilar till salu
            </Link>
          </div>
        </section>

        {/* CTA */}
        <section className="rounded-xl bg-primary text-primary-foreground p-8 text-center">
          <h2 className="text-2xl font-bold mb-3">Redo att prata om husbil?</h2>
          <p className="mb-6 opacity-90">Ring oss — vi känner vägen till {o.name} väl.</p>
          <a href="tel:0708629743" className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-background text-foreground font-semibold hover:opacity-90">
            <Phone className="w-4 h-4" /> 070-862 97 43
          </a>
        </section>

        {/* Modeller för orten */}
        {ortModeller.length > 0 && (
          <section>
            <h2 className="text-2xl font-bold mb-4">Rapido-modeller för {o.name}</h2>
            <p className="text-muted-foreground mb-4">
              Vi har räknat på vikt, längd och restid för varje modell utifrån {o.name} — se vilken
              som passar din uppställningsplats och dina resor.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {ortModeller.map((m) => (
                <Link
                  key={m.slug}
                  to="/husbil/$ort/$modell"
                  params={{ ort: o.slug, modell: m.slug }}
                  className="flex items-center justify-between px-4 py-3 rounded-lg border hover:bg-muted transition"
                >
                  <span>{m.name} i {o.name}</span>
                  <ArrowRight className="w-4 h-4 opacity-50" />
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Andra orter */}

        <section>
          <h2 className="text-xl font-bold mb-4">Vi tar även kunder från</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {otherOrter.map((x) => {
              const dedicatedPath = dedicatedOrtPath(x.slug);
              const content = (
                <>
                  <span>{x.name}</span>
                  <ArrowRight className="w-4 h-4 opacity-50" />
                </>
              );
              const className = "flex items-center justify-between px-4 py-3 rounded-lg border hover:bg-muted transition";
              return dedicatedPath ? (
                <Link key={x.slug} to={dedicatedPath} className={className}>{content}</Link>
              ) : (
                <Link key={x.slug} to="/husbil/$ort" params={{ ort: x.slug }} className={className}>{content}</Link>
              );
            })}
          </div>
        </section>
      </main>
    </>
  );
}
